// The staff side of the app, as the taker-side screens see it: the password sign-in (the same
// Better Auth call /admin makes) and whether this browser holds a staff session. Plain fetches
// through lib/api, so the taker bundle never pulls in the Better Auth client (only the
// lazy-loaded admin does).

import { useEffect, useState } from "react";
import { get, request } from "./api";

/**
 * Staff email and password: POST /v1/auth/sign-in/email, Better Auth's endpoint with every
 * check it has (origin, rate limits, the hooks in apps/api/src/staff/auth.ts). The session
 * arrives as its httpOnly cookie; the body isn't needed. A wrong password and an unknown email
 * both fail with a 401 (ApiError), so the answer never says whether the email is staff.
 */
export async function staffSignInWithPassword(email: string, password: string): Promise<void> {
  await request<unknown>("POST", "/v1/auth/sign-in/email", { body: { email, password }, noAuth: true });
}

/**
 * Ends the staff session on this browser (Better Auth's sign-out, by cookie). Used where a
 * taker-side screen signs out or offers to, so a staff session doesn't linger on a shared
 * device. Failure is tolerated by callers: the person is leaving either way.
 */
export async function staffSignOut(): Promise<void> {
  await request<unknown>("POST", "/v1/auth/sign-out", { noAuth: true });
}

/** The staff member signed in on this browser. */
export interface StaffSignedIn {
  email: string;
}

/**
 * Whether this browser is signed in as staff, by asking the staff "who am I" endpoint with the
 * session cookie alone (never a take token). It answers before two-factor too (the admin asks
 * for the code on arrival), so a "Go to admin" link can show. Takers and strangers get null
 * (401), as does a staff account whose role grants nothing (403) and any other failure.
 */
export async function staffSignedIn(): Promise<StaffSignedIn | null> {
  try {
    const me = await get<{ user: { email: string } }>("/v1/admin/me", { noAuth: true });
    return typeof me?.user?.email === "string" ? { email: me.user.email } : null;
  } catch {
    return null;
  }
}

/**
 * The staff session on this browser, once checked (null until then and when there is none),
 * and a way to end it: `signOut` asks the server (tolerating failure) and forgets it here.
 */
export function useStaffSignedIn(): { staff: StaffSignedIn | null; signOut: () => Promise<void> } {
  const [staff, setStaff] = useState<StaffSignedIn | null>(null);
  useEffect(() => {
    let live = true;
    void staffSignedIn().then((s) => live && setStaff(s));
    return () => {
      live = false;
    };
  }, []);
  const signOut = async () => {
    await staffSignOut().catch(() => undefined);
    setStaff(null);
  };
  return { staff, signOut };
}
